import styled, { css } from 'styled-components';
import { opacify, px2rem, inPixels } from '../utils/helpers/style.js';
import { styles } from '../constants/styles.js';
import { boxStyles } from '../Box/styles.js';
import { fontFamilyStyles } from '../ContentText/styles.web.js';
import { getPlainFocusBg } from '../Button/utils.js';
import { timings } from '../constants/timings.js';
import { mostReadable } from '@ctrl/tinycolor';
import { boxStyledOptions } from '../utils/helpers/styled.js';
import { themeVariant } from '../utils/helpers/themeVariant.web.js';

var wrapper = /*#__PURE__*/styled.div.withConfig(boxStyledOptions).withConfig({
  displayName: "styles__wrapper",
  componentId: "sc-1cxmoqp-0"
})(["display:flex;position:relative;align-items:stretch;", " ", ";"], function (props) {
  return props.$withBorder && css(["&:after{background:", ";bottom:0;content:'';height:", ";left:0;position:absolute;width:100%;z-index:1;}"], props.$transparent ? opacify(props.theme.tokens.border.desaturated, 0.2) : props.theme.tabs.web.borders.color, px2rem(2));
}, boxStyles);
var label = /*#__PURE__*/styled.button.withConfig({
  displayName: "styles__label",
  componentId: "sc-1cxmoqp-1"
})(["", ";text-decoration:none;outline:none;border:none;cursor:pointer;color:", ";opacity:", ";display:inline-block;padding:0 ", ";position:relative;transition:color ", ";transition-timing-function:", ";margin-left:", ";&:first-child{margin-left:0;}border-radius:", ";", " &:focus-visible{box-shadow:inset 0 0 0 2px ", ",inset 0 0 0 4px ", ";outline:none;}&:focus:not(:focus-visible){outline:none;}&:hover{background-color:", ";}&:after{background:", ";bottom:0;content:'';height:", ";left:0;position:absolute;transition:height ", ";transition-timing-function:", ";width:100%;z-index:2;}font-size:", ";line-height:", ";", "{font-size:", ";line-height:", ";}"], fontFamilyStyles, function (props) {
  return props.$textColor;
}, function (props) {
  return props.$active || props.$transparent ? 1 : 0.8;
}, themeVariant({
  normal: inPixels(32),
  condensed: inPixels(16)
}), styles.transition.timing.slower, styles.transition["function"].base, function (props) {
  return px2rem(props.theme.tabs.web.gap);
}, function (props) {
  return px2rem(props.theme.tabs.web.borders.radius);
}, function (props) {
  var mode = props.theme.tabs.web.active.mode;
  if (props.$active && mode === 'fill') {
    return css(["background-color:", ";"], props.$underlineColor || props.theme.tabs.web.color);
  } else {
    return css(["background-color:", ";"], props.$transparent ? 'transparent' : props.theme.tabs.web.inactive.background);
  }
}, function (p) {
  return p.theme.tokens.focus.primary;
}, function (p) {
  return p.theme.tokens.focus.secondary;
}, function (p) {
  if (p.$transparent) {
    return opacify(p.theme.tokens.button.desaturated, 0.2);
  }
  return p.$active ? getPlainFocusBg(p.$underlineColor, p.theme.tabs.web.active.focusHighlight) : getPlainFocusBg(p.$underlineColor, 'lighten');
}, function (props) {
  return props.$transparent ? props.theme.tokens.border.desaturated : props.$underlineColor;
}, function (props) {
  var _props$theme$tabs$web;
  return props.$active ? themeVariant({
    normal: props.theme.tabs.web.selectedBorderWidth,
    condensed: (_props$theme$tabs$web = props.theme.tabs.web.variants.condensed) === null || _props$theme$tabs$web === undefined ? undefined : _props$theme$tabs$web.selectedBorderWidth
  }) : 0;
}, styles.transition.timing.slower, styles.transition["function"].base, themeVariant({
  normal: function normal(p) {
    return p.theme.tabs.web.fontSize.base;
  },
  condensed: function condensed(p) {
    var _p$theme$tabs$web$var;
    return (_p$theme$tabs$web$var = p.theme.tabs.web.variants.condensed) === null || _p$theme$tabs$web$var === undefined || (_p$theme$tabs$web$var = _p$theme$tabs$web$var.fontSize) === null || _p$theme$tabs$web$var === undefined ? undefined : _p$theme$tabs$web$var.base;
  }
}), themeVariant({
  normal: function normal(p) {
    return p.theme.tabs.web.height["default"];
  },
  condensed: function condensed(p) {
    var _p$theme$tabs$web$var2;
    return (_p$theme$tabs$web$var2 = p.theme.tabs.web.variants.condensed) === null || _p$theme$tabs$web$var2 === undefined || (_p$theme$tabs$web$var2 = _p$theme$tabs$web$var2.height) === null || _p$theme$tabs$web$var2 === undefined ? undefined : _p$theme$tabs$web$var2["default"];
  }
}), styles.breakpoint.s, function (props) {
  return px2rem(pickTextSize(props));
}, themeVariant({
  normal: function normal(p) {
    return p.theme.tabs.web.height.s;
  },
  condensed: function condensed(p) {
    var _p$theme$tabs$web$var3;
    return (_p$theme$tabs$web$var3 = p.theme.tabs.web.variants.condensed) === null || _p$theme$tabs$web$var3 === undefined || (_p$theme$tabs$web$var3 = _p$theme$tabs$web$var3.height) === null || _p$theme$tabs$web$var3 === undefined ? undefined : _p$theme$tabs$web$var3.s;
  }
}));
function pickTextSize(props) {
  switch (props.$textSize) {
    case 'L':
      return props.theme.tabs.web.fontSize.L;
    case 'M':
      return props.theme.tabs.web.fontSize.M;
    case 'S':
      return props.theme.tabs.web.fontSize.S;
    case 'XS':
      return props.theme.tabs.web.fontSize.XS;
  }
}
var scrollIndicator = /*#__PURE__*/css(["width:30px;height:", ";position:absolute;z-index:1;opacity:0;pointer-events:none;content:'';transition:opacity ", "ms;background:transparent;", "{height:", ";}"], function (p) {
  return p.theme.tabs.web.height["default"];
}, timings.slower, styles.breakpoint.s, function (p) {
  return p.theme.tabs.web.height.s;
});
var getScrollIndicatorLuminosity = function getScrollIndicatorLuminosity(color) {
  return function (props) {
    var _mostReadable;
    return (_mostReadable = mostReadable(color, [props.theme.colors.text, props.theme.colors.background])) === null || _mostReadable === undefined ? undefined : _mostReadable.setAlpha(0.6).toHex8String();
  };
};

/* stylelint-disable -- the parser cannot survive these template literals */
var tabs = /*#__PURE__*/styled.div.withConfig({
  displayName: "styles__tabs",
  componentId: "sc-1cxmoqp-2"
})(["margin:0;padding:0;list-style:none;display:flex;overflow-x:auto;white-space:nowrap;background-color:", ";transition:opacity ", ";transition-timing-function:", ";&:after{margin-left:auto;right:0;box-shadow:inset -10px 0 8px -8px ", ";", "}&:before{margin-right:auto;left:0;box-shadow:inset 10px 0 8px -8px ", ";", "}", " ", " &::-webkit-scrollbar{display:none;}-ms-overflow-style:none;scrollbar-width:none;"], function (props) {
  return props.$backgroundColor ? props.$backgroundColor : props.$transparent ? 'transparent' : props.theme.tabs.web.background;
}, styles.transition.timing.slower, styles.transition["function"].base, function (p) {
  return getScrollIndicatorLuminosity(p.$backgroundColor ? p.$backgroundColor : p.theme.tabs.web.background);
}, scrollIndicator, function (p) {
  return getScrollIndicatorLuminosity(p.$backgroundColor ? p.$backgroundColor : p.theme.tabs.web.background);
}, scrollIndicator, function (p) {
  return p.$scrolling && !p.$scrollEndRight && "\n      &:after {\n        opacity: 1;\n      }\n    ";
}, function (p) {
  return p.$scrolling && !p.$scrollEndLeft && "\n      &:before {\n        opacity: 1;\n      }\n    ";
});

export { getScrollIndicatorLuminosity, label, tabs, wrapper };
